import type { ChangeEvent } from "react";
import { HelpPopover } from "../../components/HelpPopover.tsx";
type IntangibleRating = { mode: "raw" | "relative" | "absolute"; value: number };

const IntangibleRatingForm = ({
	intangibleRating,
	handleChange,
	playerRatings,
	godMode,
}: {
	intangibleRating?: IntangibleRating;
	handleChange: (
		type: string,
		field: string,
		event:
			| ChangeEvent<HTMLInputElement | HTMLSelectElement>
			| {
					target: {
						value: any;
					};
			  },
	) => void;
	playerRatings: any;
	godMode: boolean;
}) => {
	const currentMode = intangibleRating?.mode ?? "raw";
	const currentValue = intangibleRating?.value ?? 0;

	// Get raw OVR from player ratings with proper validation to prevent NaN
	const getRawOvr = (): number => {
		if (!playerRatings || typeof playerRatings.ovr !== 'number' || Number.isNaN(playerRatings.ovr)) {
			return 0;
		}
		return Math.max(0, Math.min(100, playerRatings.ovr));
	};

	const rawOvr = getRawOvr();

	// Calculate displayed OVR based on mode and value
	const getDisplayedOvr = (): number => {
		if (!intangibleRating || currentMode === 'raw') {
			return rawOvr;
		}
		
		if (currentMode === 'relative') {
			return Math.max(0, Math.min(100, rawOvr + currentValue));
		} else if (currentMode === 'absolute') {
			return Math.max(0, Math.min(100, currentValue));
		}
		
		return rawOvr;
	};

	const displayedOvr = getDisplayedOvr();

	return (
		<div className="mb-3">
			<label className="form-label d-flex align-items-center">
				Intangible Rating
				<HelpPopover title="Intangible Rating">
					<p>
						Intangible ratings create a "mask" for player OVR that affects
						displayed ratings, roster decisions, and playing time, but preserves
						raw ability for in-game production.
					</p>
					<p>
						<b>Raw OVR:</b> The actual calculated player ability (used for game
						simulation)
					</p>
					<p>
						<b>Displayed OVR:</b> Raw OVR modified by intangible rating (used for
						UI displays and roster management)
					</p>
					<ul>
						<li>
							<b>Raw Mode:</b> Display raw OVR (default behavior)
						</li>
						<li>
							<b>Relative Mode:</b> Add/subtract from raw OVR (e.g., -10, +15)
						</li>
						<li>
							<b>Absolute Mode:</b> Set exact displayed OVR (e.g., 50, 65)
						</li>
					</ul>
				</HelpPopover>
			</label>

			<div className="mb-2">
				<label className="form-label">Mode</label>
				<select
					className="form-select"
					onChange={handleChange.bind(null, "intangibleRating", "mode")}
					value={currentMode}
					disabled={!godMode}
				>
					<option value="raw">Raw OVR (No Modification)</option>
					<option value="relative">Relative (Add/Subtract)</option>
					<option value="absolute">Absolute (Exact Value)</option>
				</select>
			</div>

			{currentMode === "relative" && (
				<div className="mb-2">
					<label className="form-label">
						Adjustment (-100 to +100)
						<HelpPopover title="Relative Adjustment">
							Enter a positive number to increase displayed OVR, or a negative
							number to decrease it. This offset is maintained across seasons.
						</HelpPopover>
					</label>
					<input
						type="number"
						className="form-control"
						onChange={handleChange.bind(null, "intangibleRating", "value")}
						value={currentValue}
						min={-100}
						max={100}
						disabled={!godMode}
						placeholder="e.g., -10, +15"
					/>
					<div className="form-text">
						Current: Raw OVR {rawOvr} + ({currentValue}) = {displayedOvr}
					</div>
				</div>
			)}

			{currentMode === "absolute" && (
				<div className="mb-2">
					<label className="form-label">
						Exact OVR (0-100)
						<HelpPopover title="Absolute OVR">
							Set the exact displayed OVR regardless of raw ability. This value
							is maintained across seasons even as raw OVR changes.
						</HelpPopover>
					</label>
					<input
						type="number"
						className="form-control"
						onChange={handleChange.bind(null, "intangibleRating", "value")}
						value={currentValue}
						min={0}
						max={100}
						disabled={!godMode}
						placeholder="e.g., 50, 65"
					/>
					<div className="form-text">
						Current: Raw OVR {rawOvr} → Displayed {displayedOvr}
					</div>
				</div>
			)}

			<div className="alert alert-info">
				<div className="d-flex justify-content-between align-items-center">
					<div>
						<strong>Raw OVR:</strong> {rawOvr}
						<br />
						<strong>Displayed OVR:</strong> {displayedOvr}
					</div>
					<div className="text-end">
						{godMode ? (
							<button
								type="button"
								className="btn btn-sm btn-outline-secondary"
								onClick={handleChange.bind(null, "intangibleRating", "clear", {
									target: { value: "" },
								})}
							>
								Clear
							</button>
						) : null}
					</div>
				</div>
				<div className="mt-2">
					<small>
						{displayedOvr !== rawOvr
							? `This player's displayed rating is ${displayedOvr > rawOvr ? "higher" : "lower"} than their actual ability by ${Math.abs(displayedOvr - rawOvr)} points.` 
							: "This player's displayed rating matches their actual ability."}
					</small>
				</div>
			</div>
		</div>
	);
};

export default IntangibleRatingForm;
